if (!customElements.get('testimonials-component')) {
  class Testimonials extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        sliderWrapper: '.testimonials__items',
        pagination: '.swiper-pagination',
        nextEl: '.swiper-btn-next',
        prevEl: '.swiper-btn-prev',
      };
      this.classes = {
        grid: 'f-grid',
        swiper: 'swiper',
        swiperWrapper: 'swiper-wrapper',
      };

      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`.section-${this.sectionId}`);
      this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);

      this.enableSlider = this.dataset.enableSlider === 'true';
      this.items = parseInt(this.dataset.items);
      this.tabletItems = parseInt(this.dataset.tabletItems);
      this.tabletLargeItems = parseInt(this.dataset.tabletLargeItems);

      this.sliderInstance = false;
      this.progressAbort = null;

      // Mobile swipe arrows + progress bar work even when the desktop slider is off
      this.initMobileSwipeNav();
      this.initMobileProgressBar();
      this.initReadMore();

      if (!this.enableSlider) return;

      this.init();
      document.addEventListener('matchMobile', () => {
        this.init();
      });
      document.addEventListener('unmatchMobile', () => {
        this.init();
      });
    }

    init() {
      if (FoxTheme.config.mqlMobile) {
        this.destroySlider();
      } else {
        this.initSlider();
      }
    }

    initSlider() {
      const columnGap = FoxTheme.utils.getGridColumnGap(this.sliderWrapper);

      const sliderOptions = {
        slidesPerView: 2,
        spaceBetween: columnGap.mobile,
        navigation: {
          nextEl: this.section.querySelector(this.selectors.nextEl),
          prevEl: this.section.querySelector(this.selectors.prevEl),
        },
        pagination: false,
        breakpoints: {
          768: {
            slidesPerView: parseInt(this.tabletItems),
            spaceBetween: columnGap.tablet
          },
          1024: {
            slidesPerView: parseInt(this.tabletLargeItems),
            spaceBetween: columnGap.tabletLarge
          },
          1280: {
            slidesPerView: parseInt(this.items),
            spaceBetween: columnGap.desktop
          },
        },
        loop: false,
        threshold: 2,
      };

      if (typeof this.sliderInstance !== 'object') {
        this.classList.add(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.grid);
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);
        this.sliderInstance = new window.FoxTheme.Carousel(this, sliderOptions);
        this.sliderInstance.init();

        const refreshSlider = () => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            this.sliderInstance.slider.update();
          }
        };
        requestAnimationFrame(refreshSlider);
        setTimeout(refreshSlider, 300);
        window.addEventListener('load', refreshSlider, { once: true });
        setTimeout(() => this.updateReadMoreButtons?.(), 350);

        // Manual draggable progress bar (Scrollbar module is tree-shaken from vendor.js)
        const progressBarEl = this.section ? this.section.querySelector('.gh-swiper-scrollbar') : null;
        if (progressBarEl) {
          this.initProgressBar(progressBarEl);
        }

        const focusableElements = FoxTheme.a11y.getFocusableElements(this);

        focusableElements.forEach((element) => {
          element.addEventListener('focusin', () => {
            const slide = element.closest('.swiper-slide');
            this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
          });
        });
      }
      if (Shopify.designMode && typeof this.sliderInstance === 'object') {
        document.addEventListener('shopify:block:select', (e) => {
          if (e.detail.sectionId != this.sectionId) return;
          let { target } = e;
          const index = Number(target.dataset.index);

          this.sliderInstance.slider.slideTo(index);
        });
      }
    }

    initProgressBar(trackEl) {
      if (this.progressAbort) {
        this.progressAbort.abort();
      }
      this.progressAbort = new AbortController();
      const { signal } = this.progressAbort;

      let drag = trackEl.querySelector('.swiper-scrollbar-drag');
      if (!drag) {
        drag = document.createElement('div');
        drag.className = 'swiper-scrollbar-drag';
        trackEl.appendChild(drag);
      }

      const sw = this.sliderInstance.slider;
      const getPerView = () => {
        let perView = sw.params.slidesPerView;
        if (typeof perView !== 'number' || !isFinite(perView)) {
          perView = (typeof sw.slidesPerViewDynamic === 'function' ? sw.slidesPerViewDynamic() : 1) || 1;
        }
        return perView;
      };

      const updateBar = () => {
        const total = sw.slides.length;
        const perView = getPerView();
        const progress = sw.progress;
        const thumbWidth = Math.min(100, Math.max(10, (perView / Math.max(total, 1)) * 100));
        drag.style.display = total > perView ? 'block' : 'none';
        drag.style.width = (thumbWidth + progress * (100 - thumbWidth)) + '%';
        drag.style.transform = 'none';
      };

      sw.on('progress', updateBar);
      sw.on('slideChange', updateBar);
      sw.on('transitionEnd', updateBar);
      sw.on('breakpoint', updateBar);
      updateBar();

      let isDragging = false;
      let startX = 0;
      let startProgress = 0;

      const onDragStart = (e) => {
        if (FoxTheme.config.mqlMobile) return;
        isDragging = true;
        startX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
        startProgress = sw.progress;
        e.preventDefault();
      };
      const onDragMove = (e) => {
        if (!isDragging) return;
        const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
        const dx = clientX - startX;
        const trackWidth = trackEl.getBoundingClientRect().width;
        const perView = getPerView();
        const total = sw.slides.length;
        const thumbWidth = (perView / total) * trackWidth;
        const range = Math.max(1, trackWidth - thumbWidth);
        const newProgress = Math.min(1, Math.max(0, startProgress + dx / range));
        const targetIdx = Math.round(newProgress * (total - perView));
        sw.slideTo(targetIdx);
        e.preventDefault();
      };
      const onDragEnd = () => { isDragging = false; };

      drag.addEventListener('mousedown', onDragStart, { passive: false, signal });
      drag.addEventListener('touchstart', onDragStart, { passive: false, signal });
      document.addEventListener('mousemove', onDragMove, { passive: false, signal });
      document.addEventListener('touchmove', onDragMove, { passive: false, signal });
      document.addEventListener('mouseup', onDragEnd, { signal });
      document.addEventListener('touchend', onDragEnd, { signal });
    }

    initReadMore() {
      const modal = this.section ? this.section.querySelector('[data-testimonial-modal]') : null;
      if (!modal) return;

      const modalContent = modal.querySelector('[data-testimonial-modal-content]');
      const modalAuthor = modal.querySelector('[data-testimonial-modal-author]');
      const modalBio = modal.querySelector('[data-testimonial-modal-bio]');
      const modalIcon = modal.querySelector('[data-testimonial-modal-icon]');
      const WORD_LIMIT = 20;
      this.fullContentStore = this.fullContentStore || new WeakMap();

      const getWords = (text) => text.trim().split(/\s+/).filter(Boolean);

      const getTextFromHtml = (html) => {
        const temp = document.createElement('div');
        temp.innerHTML = html;
        return temp.textContent || '';
      };

      const getModalContent = (html) => {
        const temp = document.createElement('div');
        temp.innerHTML = html;
        temp.querySelectorAll('p').forEach((el) => {
          el.textContent = (el.textContent || '').replace(/(\.\.\.|…)+\s*$/, '').trimEnd();
        });
        return temp.innerHTML;
      };

      const updateReadMoreButtons = () => {
        this.querySelectorAll('.testimonial').forEach((card) => {
          if (card.closest('.swiper-slide-duplicate')) return;

          const content = card.querySelector('.testimonial__content');
          const button = card.querySelector('.testimonial-card__button');
          if (!content || !button) return;

          if (!this.fullContentStore.has(content)) {
            this.fullContentStore.set(content, content.innerHTML);
          }

          const fullHtml = this.fullContentStore.get(content);
          const words = getWords(getTextFromHtml(fullHtml));
          const showReadMore = words.length > WORD_LIMIT;

          if (showReadMore) {
            const truncated = document.createElement('p');
            truncated.textContent = `${words.slice(0, WORD_LIMIT).join(' ')}...`;
            content.innerHTML = '';
            content.appendChild(truncated);
            content.classList.add('testimonial__content--truncated');
          } else {
            content.innerHTML = fullHtml;
            content.classList.remove('testimonial__content--truncated');
          }

          button.classList.toggle('hidden', !showReadMore);
          button.hidden = !showReadMore;
        });
      };

      this.updateReadMoreButtons = updateReadMoreButtons;

      this.addEventListener('click', (event) => {
        const button = event.target.closest('.testimonial-card__button');
        if (!button || !this.contains(button)) return;

        event.preventDefault();
        event.stopPropagation();
        const card = button.closest('.testimonial');
        if (!card) return;

        const content = card.querySelector('.testimonial__content');
        const author = card.querySelector('.testimonial__name');
        const bio = card.querySelector('.testimonial__bio');
        const icon = card.querySelector('.testimonial__icon');
        const fullHtml = content ? this.fullContentStore.get(content) : null;

        if (modalContent && fullHtml) {
          modalContent.innerHTML = getModalContent(fullHtml);
        }
        if (modalAuthor) {
          modalAuthor.textContent = author ? author.textContent.trim() : '';
          modalAuthor.hidden = !author;
        }
        if (modalBio) {
          modalBio.textContent = bio ? bio.textContent.trim() : '';
          modalBio.hidden = !bio;
        }
        if (modalIcon) {
          if (icon) {
            modalIcon.innerHTML = icon.innerHTML;
            modalIcon.hidden = false;
          } else {
            modalIcon.innerHTML = '';
            modalIcon.hidden = true;
          }
        }

        modal.show(button);
      });

      updateReadMoreButtons();
      window.addEventListener('load', updateReadMoreButtons, { once: true });
    }

    enableMobileNavButtons() {
      if (this.dataset.enableSwipeMobile !== 'true' || !FoxTheme.config.mqlMobile || !this.section) return;
      [this.selectors.prevEl, this.selectors.nextEl].forEach((sel) => {
        const btn = this.section.querySelector(sel);
        if (!btn) return;
        btn.classList.remove('swiper-button-disabled', 'swiper-button-lock', 'swiper-button-hidden');
        btn.removeAttribute('aria-disabled');
        btn.style.pointerEvents = 'auto';
      });
    }

    initMobileSwipeNav() {
      if (this.dataset.enableSwipeMobile !== 'true' || !this.section) return;

      const prevBtn = this.section.querySelector(this.selectors.prevEl);
      const nextBtn = this.section.querySelector(this.selectors.nextEl);
      if (!prevBtn || !nextBtn) return;

      this.enableMobileNavButtons();

      const scrollByColumn = (direction) => {
        const inner = this.sliderWrapper;
        let step = this.clientWidth * 0.7;
        const firstItem = inner && inner.children ? inner.children[0] : null;
        if (firstItem) {
          const gap = parseFloat(getComputedStyle(inner).columnGap) || 0;
          step = firstItem.getBoundingClientRect().width + gap;
        }
        this.scrollBy({ left: direction * step, behavior: 'smooth' });
      };

      // Only act on mobile; on desktop these buttons are driven by Swiper navigation.
      prevBtn.addEventListener('click', () => {
        if (FoxTheme.config.mqlMobile) scrollByColumn(-1);
      });
      nextBtn.addEventListener('click', () => {
        if (FoxTheme.config.mqlMobile) scrollByColumn(1);
      });
    }

    initMobileProgressBar() {
      if (this.dataset.enableSwipeMobile !== 'true' || !this.section) return;
      const trackEl = this.section.querySelector('.gh-swiper-scrollbar');
      if (!trackEl) return;

      let drag = trackEl.querySelector('.swiper-scrollbar-drag');
      if (!drag) {
        drag = document.createElement('div');
        drag.className = 'swiper-scrollbar-drag';
        trackEl.appendChild(drag);
      }

      const updateBar = () => {
        if (!FoxTheme.config.mqlMobile) return;
        const scrollable = this.scrollWidth - this.clientWidth;
        const thumbWidth = Math.min(100, Math.max(10, (this.clientWidth / this.scrollWidth) * 100));
        const progress = scrollable > 0 ? this.scrollLeft / scrollable : 0;
        const maxLeft = 100 - thumbWidth;
        drag.style.display = scrollable > 1 ? 'block' : 'none';
        drag.style.width = (thumbWidth + progress * (100 - thumbWidth)) + '%';
        drag.style.transform = 'none';
      };

      this.addEventListener('scroll', updateBar, { passive: true });
      window.addEventListener('resize', FoxTheme.utils.debounce(updateBar, 100));
      window.addEventListener('load', updateBar, { once: true });
      requestAnimationFrame(updateBar);

      // Draggable on mobile -> drives native scroll position
      let isDragging = false;
      let startX = 0;
      let startScroll = 0;

      const onDragStart = (e) => {
        if (!FoxTheme.config.mqlMobile) return;
        isDragging = true;
        startX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
        startScroll = this.scrollLeft;
        e.preventDefault();
      };
      const onDragMove = (e) => {
        if (!isDragging || !FoxTheme.config.mqlMobile) return;
        const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
        const dx = clientX - startX;
        const trackWidth = trackEl.getBoundingClientRect().width;
        const scrollable = this.scrollWidth - this.clientWidth;
        const thumbWidth = (this.clientWidth / this.scrollWidth) * trackWidth;
        const range = Math.max(1, trackWidth - thumbWidth);
        this.scrollLeft = startScroll + (dx / range) * scrollable;
        e.preventDefault();
      };
      const onDragEnd = () => { isDragging = false; };

      drag.addEventListener('mousedown', onDragStart, { passive: false });
      drag.addEventListener('touchstart', onDragStart, { passive: false });
      document.addEventListener('mousemove', onDragMove, { passive: false });
      document.addEventListener('touchmove', onDragMove, { passive: false });
      document.addEventListener('mouseup', onDragEnd);
      document.addEventListener('touchend', onDragEnd);
    }

    destroySlider() {
      this.classList.remove(this.classes.swiper);
      this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
      this.sliderWrapper.classList.add(this.classes.grid);
      this.enableMobileNavButtons();
      if (this.progressAbort) {
        this.progressAbort.abort();
        this.progressAbort = null;
      }
      if (typeof this.sliderInstance === 'object') {
        this.sliderInstance.slider.destroy();
        this.sliderInstance = false;
      }
    }
  }
  customElements.define('testimonials-component', Testimonials);
}